/**
 * Display obfuscation for contact methods (emails/phones) whose full value
 * belongs to a DIFFERENT account than the one viewing them — e.g. the
 * account-merge verify-method picker, where the backend sends the other
 * user's methods raw and the client obfuscates (vingapi contract, uptake
 * #4216).
 *
 * Extracted verbatim from MergeSuggestionBanner when the merge UI moved to
 * AccountMergeDialog.
 */

import { isEmail } from '@/utils/contactMethodUtils';

/**
 * Obfuscate email for display
 * e.g., john.doe@example.com -> j******e@ex***le.com
 */
export function obfuscateEmail(email: string): string {
  const [username, domain] = email.split('@');
  if (!username || !domain) return email;

  const [domainName, ...ext] = domain.split('.');
  if (!domainName) return email;
  const extension = ext.join('.');

  const obfuscatedUsername =
    username.length <= 2
      ? username
      : (username[0] ?? '') + '*'.repeat(Math.min(6, username.length - 2)) + (username[username.length - 1] ?? '');

  const obfuscatedDomain =
    domainName.length <= 4
      ? domainName
      : domainName.slice(0, 2) + '*'.repeat(Math.min(3, domainName.length - 4)) + domainName.slice(-2);

  return `${obfuscatedUsername}@${obfuscatedDomain}.${extension}`;
}

/**
 * Obfuscate phone for display
 * e.g., +1 (555) 123-4567 -> +1 (5**) ***-4567
 */
export function obfuscatePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) return phone;

  const visible = digits.slice(-4);
  const hidden = digits.slice(0, -4);
  const obfuscated = hidden.replace(/\d/g, '*');

  let result = '';
  let obfuscatedIndex = 0;
  let visibleIndex = 0;

  for (const char of phone) {
    if (/\d/.test(char)) {
      if (obfuscatedIndex < obfuscated.length) {
        result += obfuscated[obfuscatedIndex];
        obfuscatedIndex++;
      } else {
        result += visible[visibleIndex];
        visibleIndex++;
      }
    } else {
      result += char;
    }
  }

  return result;
}

/** Obfuscate either kind of contact method by shape. */
export function obfuscateContactMethod(method: string): string {
  return isEmail(method) ? obfuscateEmail(method) : obfuscatePhone(method);
}
